import React, { useState, useEffect, useContext, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from '../config/axios';
import { AuthContext } from '../context/AuthContext';
import { formatError } from '../utils/errorHandler';
import {
  canClaimOrReturnHandout,
  canAccessFeature,
  isCompanyAdmin,
  isCompanyEmployee
} from '../utils/planAccess';
import { formatDateShort, formatDateTime } from '../utils/locale';
import {
  filterTodayTasks,
  filterUpcomingTasks,
  isCompletedToday
} from '../utils/taskFilters';
import { isPendingBilling } from '../utils/taskBilling';
import { getTaskAssigneeLabel, isTaskClaimedOrAssigned } from '../utils/taskClaim';
import LoadingSpinner from '../components/LoadingSpinner';
import EmptyState from '../components/EmptyState';
import Modal from '../components/Modal';
import EndOfDayHandoutsSection from '../components/EndOfDayHandoutsSection';
import TaskBillingStatusBadges from '../components/TaskBillingStatusBadges';
import './Dashboard.css';

const DASHBOARD_BILLING_PREVIEW = 8;
const DASHBOARD_LIST_PAGE_SIZE = 10;

const INITIAL_LIST_VISIBLE = {
  overdue: DASHBOARD_LIST_PAGE_SIZE,
  today: DASHBOARD_LIST_PAGE_SIZE,
  upcoming: DASHBOARD_LIST_PAGE_SIZE
};

const Dashboard = () => {
  const { t, i18n } = useTranslation('dashboard');
  const { t: tNav } = useTranslation('nav');
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    completedToday: 0,
    today: 0,
    upcoming: 0,
    inProgress: 0
  });
  const [todayTasks, setTodayTasks] = useState([]);
  const [upcomingTasks, setUpcomingTasks] = useState([]);
  const [overdueTasks, setOverdueTasks] = useState([]);
  const [completedTodayTasks, setCompletedTodayTasks] = useState([]);
  const [pendingBillingTasks, setPendingBillingTasks] = useState([]);
  const [listVisibleCount, setListVisibleCount] = useState(INITIAL_LIST_VISIBLE);
  const listExpandedRef = useRef({ overdue: false, today: false, upcoming: false });
  const [myHandouts, setMyHandouts] = useState([]);
  const [attendanceSession, setAttendanceSession] = useState(null);
  const [attendanceModalOpen, setAttendanceModalOpen] = useState(false);
  const [attendanceForm, setAttendanceForm] = useState({
    check_in_at: '',
    check_out_at: '',
    note: '',
    took_lunch: false,
    lunch_minutes: ''
  });
  const [attendanceSubmitting, setAttendanceSubmitting] = useState(false);
  const [returningHandoutId, setReturningHandoutId] = useState(null);
  const [handoutsBlockingCheckout, setHandoutsBlockingCheckout] = useState(false);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState('');

  useEffect(() => {
    fetchDashboardData();
  }, [user]);

  const fetchDashboardData = async () => {
    try {
      setFetchError('');
      const [tasksResponse, handoutsResponse, attendanceResponse] = await Promise.all([
        axios.get('/api/tasks', { params: { limit: 100 } }),
        axios.get('/api/handouts/mine').catch(() => ({ data: { data: [] } })),
        user?.role === 'super_admin'
          ? Promise.resolve({ data: { data: null } })
          : axios.get('/api/day-attendance/current').catch(() => ({ data: { data: null } }))
      ]);
      const tasks = tasksResponse.data.data || [];
      setMyHandouts(handoutsResponse.data.data || []);
      setAttendanceSession(attendanceResponse.data.data || null);

      // Admin: all company tasks. Non-admin: only claimed/assigned to them
      // (employee API also returns unassigned claimables — exclude those here).
      const viewerIsAdmin = isCompanyAdmin(user);
      const scopedTasks = viewerIsAdmin
        ? tasks
        : tasks.filter((task) => isTaskClaimedOrAssigned(task));

      const todayDate = new Date();
      todayDate.setHours(0, 0, 0, 0);

      const todayBucket = filterTodayTasks(scopedTasks, todayDate);
      const upcoming = filterUpcomingTasks(scopedTasks, todayDate);
      const completedToday = scopedTasks.filter((task) => isCompletedToday(task, todayDate));
      const overdue = todayBucket.filter((task) => {
        if (!task.scheduled_for) return false;
        const scheduledDate = new Date(task.scheduled_for);
        scheduledDate.setHours(0, 0, 0, 0);
        return scheduledDate.getTime() < todayDate.getTime();
      });
      const overdueIds = new Set(overdue.map((task) => task.id));
      const todayOnly = todayBucket.filter((task) => !overdueIds.has(task.id));
      const pendingBilling = isCompanyAdmin(user)
        ? tasks.filter(isPendingBilling).slice(0, DASHBOARD_BILLING_PREVIEW)
        : [];

      setStats({
        completedToday: completedToday.length,
        today: todayBucket.length,
        upcoming: upcoming.length,
        inProgress: scopedTasks.filter((task) => task.status === 'in_progress').length
      });
      setTodayTasks(todayOnly);
      setUpcomingTasks(upcoming);
      setOverdueTasks(overdue);
      setCompletedTodayTasks(completedToday);
      setPendingBillingTasks(pendingBilling);
      setListVisibleCount(INITIAL_LIST_VISIBLE);
      listExpandedRef.current = { overdue: false, today: false, upcoming: false };
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setFetchError(formatError(error) || t('failedLoad'));
    } finally {
      setLoading(false);
    }
  };

  const handleTaskClick = (taskId) => {
    navigate(`/tasks/${taskId}`);
  };

  const handleListShowMore = (sectionKey, tasksPath) => {
    if (!listExpandedRef.current[sectionKey]) {
      listExpandedRef.current[sectionKey] = true;
      setListVisibleCount((prev) => ({
        ...prev,
        [sectionKey]: prev[sectionKey] + DASHBOARD_LIST_PAGE_SIZE
      }));
      return;
    }
    navigate(tasksPath);
  };

  const renderListShowMore = (sectionKey, totalCount, tasksPath) => {
    const visible = listVisibleCount[sectionKey];
    if (totalCount <= visible) {
      return null;
    }
    return (
      <button
        type="button"
        className="dashboard-show-more"
        onClick={() => handleListShowMore(sectionKey, tasksPath)}
      >
        {t('showMore')}
      </button>
    );
  };

  const renderTaskCard = (task, statusPillClass, statusLabel, dateMeta) => {
    const assigneeLabel = getTaskAssigneeLabel(task);
    return (
      <div
        key={task.id}
        className="sim-task-card dashboard-task-card"
        onClick={() => handleTaskClick(task.id)}
      >
        <div className="dashboard-task-card-content">
          <h3 className="sim-task-card-title">{task.title}</h3>
          <div className="sim-task-card-meta dashboard-task-card-meta">
            {task.location_name && (
              <span className="dashboard-task-meta-item">
                <span className="material-symbols-outlined">location_on</span>
                {task.location_name}
              </span>
            )}
            {dateMeta}
            {assigneeLabel && (
              <span className="dashboard-task-meta-item">
                <span className="material-symbols-outlined">person</span>
                {assigneeLabel}
              </span>
            )}
          </div>
        </div>
        <div className={`sim-status-pill ${statusPillClass}`}>{statusLabel}</div>
      </div>
    );
  };

  const showTimeReport = user?.role !== 'super_admin';
  const showHandoutsLink = canAccessFeature(user, 'handouts');
  const showEodHandouts = showHandoutsLink && user?.role !== 'super_admin';
  const showBillingSection = isCompanyAdmin(user);

  const handleReturnHandout = async (handoutId) => {
    if (returningHandoutId) {
      return;
    }
    setReturningHandoutId(handoutId);
    try {
      await axios.post(`/api/handouts/${handoutId}/return`);
      setMyHandouts((prev) => prev.filter((handout) => handout.id !== handoutId));
    } catch (error) {
      console.error('Error returning handout:', error);
      setFetchError(formatError(error) || t('failedReturnHandout'));
    } finally {
      setReturningHandoutId(null);
    }
  };

  const toDateTimeLocalValue = (value) => {
    const date = new Date(value);
    const offset = date.getTimezoneOffset();
    return new Date(date.getTime() - offset * 60000).toISOString().slice(0, 16);
  };

  const openCheckoutModal = () => {
    if (!attendanceSession) return;
    setFetchError('');
    setHandoutsBlockingCheckout(false);
    setAttendanceForm({
      check_in_at: toDateTimeLocalValue(attendanceSession.check_in_at),
      check_out_at: toDateTimeLocalValue(new Date()),
      note: attendanceSession.note || '',
      took_lunch: false,
      lunch_minutes: ''
    });
    setAttendanceModalOpen(true);
  };

  const closeCheckoutModal = () => {
    setAttendanceModalOpen(false);
    setHandoutsBlockingCheckout(false);
  };

  const handleAttendanceCheckIn = async (sessionKind = 'ordinary') => {
    try {
      setAttendanceSubmitting(true);
      setFetchError('');
      const response = await axios.post('/api/day-attendance/check-in', {
        session_kind: sessionKind
      });
      setAttendanceSession(response.data.data);
    } catch (error) {
      setFetchError(formatError(error, t('failedCheckIn')));
    } finally {
      setAttendanceSubmitting(false);
    }
  };

  const isJourSession = attendanceSession?.session_kind === 'jour';
  const showJourCalloutCreate = isJourSession && isCompanyAdmin(user);
  const showJourCalloutClaim = isJourSession && isCompanyEmployee(user);

  const handleAttendanceCheckout = async (event) => {
    event.preventDefault();
    if (!attendanceSession) return;
    if (showEodHandouts && handoutsBlockingCheckout) {
      setFetchError(t('eodReturnHandoutsFirst'));
      return;
    }
    try {
      setAttendanceSubmitting(true);
      setFetchError('');
      await axios.put(`/api/day-attendance/${attendanceSession.id}/check-out`, {
        check_in_at: new Date(attendanceForm.check_in_at).toISOString(),
        check_out_at: new Date(attendanceForm.check_out_at).toISOString(),
        note: attendanceForm.note,
        lunch_minutes: attendanceForm.took_lunch ? Number.parseInt(attendanceForm.lunch_minutes, 10) : 0
      });
      setAttendanceSession(null);
      closeCheckoutModal();
    } catch (error) {
      setFetchError(formatError(error, t('failedCheckOut')));
    } finally {
      setAttendanceSubmitting(false);
    }
  };

  if (loading) {
    return <LoadingSpinner className="dashboard-loading" />;
  }

  return (
    <div className="dashboard">
      <div className="container container--wide main-content">
        <div className="dashboard-container">
        <h1 className="dashboard-title">{t('title')}</h1>
        <p className="dashboard-subtitle">
          {t('welcome', { firstName: user?.first_name, lastName: user?.last_name })}
        </p>
        <nav className="dashboard-quick-links" aria-label={t('quickLinks')}>
          <Link to="/tasks" className="dashboard-quick-link">
            <span className="material-symbols-outlined" aria-hidden="true">assignment</span>
            {tNav('tasks')}
          </Link>
          {showTimeReport && (
            <Link to="/time-report" className="dashboard-quick-link">
              <span className="material-symbols-outlined" aria-hidden="true">schedule</span>
              {tNav('timeReport')}
            </Link>
          )}
          {showHandoutsLink && (
            <Link to="/handouts" className="dashboard-quick-link">
              <span className="material-symbols-outlined" aria-hidden="true">inventory_2</span>
              {tNav('handouts')}
            </Link>
          )}
        </nav>
        {fetchError && <div className="error">{fetchError}</div>}

        {user?.role !== 'super_admin' && (
          <section
            className={`dashboard-attendance-card card${attendanceSession ? '' : ' dashboard-attendance-card--needs-checkin'}`}
          >
            <div>
              <h2 className="dashboard-section-title">{t('dayAttendance')}</h2>
              <p className="dashboard-attendance-status">
                {attendanceSession
                  ? t(isJourSession ? 'checkedInJourAt' : 'checkedInAt', {
                    datetime: formatDateTime(attendanceSession.check_in_at)
                  })
                  : t('notCheckedInToday')}
              </p>
              {(showJourCalloutCreate || showJourCalloutClaim) && (
                <p className="dashboard-attendance-callout-hint">{t('calloutHint')}</p>
              )}
            </div>
            {attendanceSession ? (
              <div className="dashboard-attendance-actions">
                {showJourCalloutCreate && (
                  <Link
                    to="/tasks?create=1"
                    className="sim-button-primary dashboard-attendance-cta"
                  >
                    {t('calloutCreate')}
                  </Link>
                )}
                {showJourCalloutClaim && (
                  <Link
                    to="/tasks?section=today"
                    className="sim-button-primary dashboard-attendance-cta"
                  >
                    {t('calloutClaim')}
                  </Link>
                )}
                <button
                  type="button"
                  className="sim-button-secondary dashboard-attendance-cta"
                  onClick={openCheckoutModal}
                  disabled={attendanceSubmitting}
                >
                  {t(isJourSession ? 'checkOutJour' : 'checkOutForDay')}
                </button>
              </div>
            ) : (
              <div className="dashboard-attendance-actions">
                <button
                  type="button"
                  className="sim-button-primary dashboard-attendance-cta"
                  onClick={() => handleAttendanceCheckIn('ordinary')}
                  disabled={attendanceSubmitting}
                >
                  {t('checkIn')}
                </button>
                <button
                  type="button"
                  className="sim-button-secondary dashboard-attendance-cta"
                  onClick={() => handleAttendanceCheckIn('jour')}
                  disabled={attendanceSubmitting}
                >
                  {t('checkInJour')}
                </button>
              </div>
            )}
          </section>
        )}

        {/* Summary chips — navigate to Tasks sections */}
        <nav className="dashboard-stats-grid" aria-label={t('taskSummaryLinks')}>
          <Link
            to="/tasks?section=today"
            className="dashboard-stat-card dashboard-stat-card--link"
          >
            <h3 className="dashboard-stat-label">{t('today')}</h3>
            <p className="dashboard-stat-number">{stats.today}</p>
          </Link>
          <Link
            to="/tasks?section=upcoming"
            className="dashboard-stat-card dashboard-stat-card--link dashboard-stat-card--upcoming"
          >
            <h3 className="dashboard-stat-label">{t('upcoming')}</h3>
            <p className="dashboard-stat-number">{stats.upcoming}</p>
          </Link>
          <Link
            to="/tasks?section=completed"
            className="dashboard-stat-card dashboard-stat-card--link dashboard-stat-card--completed"
          >
            <h3 className="dashboard-stat-label">{t('completedToday')}</h3>
            <p className="dashboard-stat-number">{stats.completedToday}</p>
          </Link>
          <Link
            to="/tasks"
            className="dashboard-stat-card dashboard-stat-card--link dashboard-stat-card--progress"
          >
            <h3 className="dashboard-stat-label">{t('inProgress')}</h3>
            <p className="dashboard-stat-number">{stats.inProgress}</p>
          </Link>
        </nav>

        {myHandouts.length > 0 && (
          <section className="dashboard-section">
            <h2 className="dashboard-section-title">{t('myHandouts')}</h2>
            <div className="dashboard-handout-list">
              {myHandouts.map((handout) => (
                <div key={handout.id} className="dashboard-handout-card">
                  <div className="dashboard-handout-card-content">
                    <h3 className="dashboard-handout-name">{handout.name}</h3>
                    {handout.location_name && (
                      <p className="dashboard-handout-location">{handout.location_name}</p>
                    )}
                    {handout.description && (
                      <p className="dashboard-handout-description">{handout.description}</p>
                    )}
                  </div>
                  {canClaimOrReturnHandout(user) && (
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      onClick={() => handleReturnHandout(handout.id)}
                      disabled={returningHandoutId === handout.id}
                    >
                      {t('return')}
                    </button>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Overdue Tasks Section */}
        {overdueTasks.length > 0 && (
          <section className="dashboard-section">
            <div className="dashboard-section-heading-row">
              <h2 className="dashboard-section-title">{t('overdueTasks')}</h2>
              <Link to="/tasks?section=today" className="dashboard-section-link">
                {t('viewAll')}
              </Link>
            </div>
            <div className="dashboard-task-list">
              {overdueTasks.slice(0, listVisibleCount.overdue).map((task) =>
                renderTaskCard(
                  task,
                  'sim-status-pill-overdue',
                  t('overdue'),
                  task.scheduled_for && (
                    <span className="dashboard-task-meta-item">
                      <span className="material-symbols-outlined">calendar_today</span>
                      {formatDateShort(task.scheduled_for, i18n.language)}
                    </span>
                  )
                )
              )}
            </div>
            {renderListShowMore('overdue', overdueTasks.length, '/tasks?section=today')}
          </section>
        )}

        {/* Today's Tasks Section */}
        {todayTasks.length > 0 && (
          <section className="dashboard-section">
            <div className="dashboard-section-heading-row">
              <h2 className="dashboard-section-title">{t('todayTasks')}</h2>
              <Link to="/tasks?section=today" className="dashboard-section-link">
                {t('viewAll')}
              </Link>
            </div>
            <div className="dashboard-task-list">
              {todayTasks.slice(0, listVisibleCount.today).map((task) =>
                renderTaskCard(
                  task,
                  'sim-status-pill-today',
                  t('today'),
                  task.scheduled_for && (
                    <span className="dashboard-task-meta-item">
                      <span className="material-symbols-outlined">calendar_today</span>
                      {t('today')}
                    </span>
                  )
                )
              )}
            </div>
            {renderListShowMore('today', todayTasks.length, '/tasks?section=today')}
          </section>
        )}

        {/* Upcoming Tasks Section */}
        <section className="dashboard-section" aria-labelledby="dashboard-upcoming-heading">
          <div className="dashboard-section-heading-row">
            <h2 id="dashboard-upcoming-heading" className="dashboard-section-title">
              {t('upcomingTasks')}
            </h2>
            <Link to="/tasks?section=upcoming" className="dashboard-section-link">
              {t('viewAll')}
            </Link>
          </div>
          {upcomingTasks.length > 0 ? (
            <>
              <div className="dashboard-task-list">
                {upcomingTasks.slice(0, listVisibleCount.upcoming).map((task) =>
                  renderTaskCard(
                    task,
                    'sim-status-pill-today',
                    t('upcoming'),
                    task.scheduled_for && (
                      <span className="dashboard-task-meta-item">
                        <span className="material-symbols-outlined">event</span>
                        {formatDateShort(task.scheduled_for, i18n.language)}
                      </span>
                    )
                  )
                )}
              </div>
              {renderListShowMore('upcoming', upcomingTasks.length, '/tasks?section=upcoming')}
            </>
          ) : (
            <EmptyState
              icon="event"
              title={t('noUpcomingTitle')}
              description={t('noUpcomingDescription')}
              actionLabel={t('viewUpcomingOnTasks')}
              onAction={() => navigate('/tasks?section=upcoming')}
            />
          )}
        </section>

        {showBillingSection && pendingBillingTasks.length > 0 && (
          <section className="dashboard-section" aria-labelledby="dashboard-billing-heading">
            <div className="dashboard-section-heading-row">
              <h2 id="dashboard-billing-heading" className="dashboard-section-title">
                {t('pendingBillingTitle')}
              </h2>
              <div className="dashboard-section-links">
                <Link to="/task-export" className="dashboard-section-link">
                  {t('pendingBillingTaskLog')}
                </Link>
              </div>
            </div>
            <p className="dashboard-section-help">{t('pendingBillingHelp')}</p>
            <div className="dashboard-task-list">
              {pendingBillingTasks.map((task) => (
                <div
                  key={task.id}
                  className="sim-task-card dashboard-task-card"
                  onClick={() => handleTaskClick(task.id)}
                >
                  <div className="dashboard-task-card-content">
                    <h3 className="sim-task-card-title">{task.title}</h3>
                    <div className="sim-task-card-meta dashboard-task-card-meta">
                      {task.location_name && (
                        <span className="dashboard-task-meta-item">
                          <span className="material-symbols-outlined">location_on</span>
                          {task.location_name}
                        </span>
                      )}
                      <TaskBillingStatusBadges task={task} iconsOnly />
                      {!task.attested && (
                        <span className="dashboard-billing-chip dashboard-billing-chip--missing">
                          {t('notAttested')}
                        </span>
                      )}
                      {!task.invoiced && (
                        <span className="dashboard-billing-chip dashboard-billing-chip--missing">
                          {t('notInvoiced')}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="sim-status-pill sim-status-pill-completed">{t('completed')}</div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Completed Today Tasks Section */}
        {completedTodayTasks.length > 0 && (
          <section className="dashboard-section">
            <div className="dashboard-section-heading-row">
              <h2 className="dashboard-section-title">{t('completedToday')}</h2>
              <Link to="/tasks?section=completed" className="dashboard-section-link">
                {t('viewAll')}
              </Link>
            </div>
            <div className="dashboard-task-list">
              {completedTodayTasks.map((task) =>
                renderTaskCard(
                  task,
                  'sim-status-pill-completed',
                  t('completed'),
                  task.updated_at && (
                    <span className="dashboard-task-meta-item">
                      <span className="material-symbols-outlined">check_circle</span>
                      {t('completedOnDate', { date: formatDateShort(task.updated_at, i18n.language) })}
                    </span>
                  )
                )
              )}
            </div>
          </section>
        )}

        </div>
      </div>
      <Modal
        isOpen={attendanceModalOpen}
        onClose={closeCheckoutModal}
        title={t(isJourSession ? 'checkoutModalTitleJour' : 'checkoutModalTitle')}
        className="dashboard-attendance-modal-panel"
      >
        <form className="dashboard-attendance-form" onSubmit={handleAttendanceCheckout}>
          <label htmlFor="dashboard-check-in">{t('checkInTime')}</label>
          <input
            id="dashboard-check-in"
            type="datetime-local"
            value={attendanceForm.check_in_at}
            onChange={(event) => setAttendanceForm((form) => ({ ...form, check_in_at: event.target.value }))}
            required
          />
          <label htmlFor="dashboard-check-out">{t('checkOutTime')}</label>
          <input
            id="dashboard-check-out"
            type="datetime-local"
            value={attendanceForm.check_out_at}
            onChange={(event) => setAttendanceForm((form) => ({ ...form, check_out_at: event.target.value }))}
            required
          />
          <label htmlFor="dashboard-attendance-note">{t('noteOptional')}</label>
          <textarea
            id="dashboard-attendance-note"
            value={attendanceForm.note}
            onChange={(event) => setAttendanceForm((form) => ({ ...form, note: event.target.value }))}
          />
          <label className="dashboard-attendance-checkbox">
            <input
              type="checkbox"
              checked={attendanceForm.took_lunch}
              onChange={(event) => setAttendanceForm((form) => ({
                ...form,
                took_lunch: event.target.checked,
                lunch_minutes: event.target.checked ? form.lunch_minutes || '30' : ''
              }))}
            />
            {t('tookLunchBreak')}
          </label>
          {attendanceForm.took_lunch && (
            <>
              <label htmlFor="dashboard-lunch-minutes">{t('lunchDurationMinutes')}</label>
              <input
                id="dashboard-lunch-minutes"
                type="number"
                min="1"
                step="1"
                value={attendanceForm.lunch_minutes}
                onChange={(event) => setAttendanceForm((form) => ({ ...form, lunch_minutes: event.target.value }))}
                required
              />
            </>
          )}
          {showEodHandouts && (
            <EndOfDayHandoutsSection
              active={attendanceModalOpen}
              user={user}
              initialHandouts={myHandouts}
              onBlockingChange={setHandoutsBlockingCheckout}
              onHandoutsChange={setMyHandouts}
              onError={(message) => setFetchError(message)}
            />
          )}
          {fetchError && attendanceModalOpen && (
            <div className="error dashboard-attendance-modal-error">{fetchError}</div>
          )}
          <div className="modal-actions">
            <button type="button" className="btn btn-secondary" onClick={closeCheckoutModal}>
              {t('cancel', { ns: 'common' })}
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={attendanceSubmitting || (showEodHandouts && handoutsBlockingCheckout)}
            >
              {attendanceSubmitting
                ? t('saving')
                : t(isJourSession ? 'checkOutJour' : 'checkOutForDay')}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default Dashboard;
